/**
 * routeAnimation.ts — Pure utility functions for tour route animation.
 * No React imports, no side effects. All functions are individually testable.
 */

import type { RouteSegment, VehiclePosition, CameraParams, CameraMode } from "./types";
import type { TravelMode } from "@/features/routebuilder/types";

const DEG2RAD = Math.PI / 180;
const RAD2DEG = 180 / Math.PI;

// ── Haversine distance ────────────────────────────────────────────────────────

/** Distance in metres between two [lng, lat] points. */
export function haversineMeters(
  [lng1, lat1]: [number, number],
  [lng2, lat2]: [number, number],
): number {
  const R = 6_371_000;
  const dLat = (lat2 - lat1) * DEG2RAD;
  const dLng = (lng2 - lng1) * DEG2RAD;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * DEG2RAD) * Math.cos(lat2 * DEG2RAD) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// ── Bearing ───────────────────────────────────────────────────────────────────

/**
 * Forward azimuth (degrees, 0=north, CW) between two [lng, lat] pairs.
 * Returns `fallback` (default 0) when points are co-located.
 */
export function computeBearing(
  [lng1, lat1]: [number, number],
  [lng2, lat2]: [number, number],
  fallback = 0,
): number {
  const dLng = (lng2 - lng1) * DEG2RAD;
  const lat1R = lat1 * DEG2RAD;
  const lat2R = lat2 * DEG2RAD;
  const y = Math.sin(dLng) * Math.cos(lat2R);
  const x = Math.cos(lat1R) * Math.sin(lat2R) - Math.sin(lat1R) * Math.cos(lat2R) * Math.cos(dLng);
  if (Math.abs(y) < 1e-10 && Math.abs(x) < 1e-10) return fallback;
  return ((Math.atan2(y, x) * RAD2DEG) + 360) % 360;
}

/** Normalise a bearing delta to the range [-180, 180]. */
function normaliseDelta(delta: number): number {
  let d = delta % 360;
  if (d > 180) d -= 360;
  if (d < -180) d += 360;
  return d;
}

// ── Arc-length accumulation ───────────────────────────────────────────────────

/**
 * Produce a parallel array of cumulative haversine distances (metres)
 * starting at 0 for a list of [lng, lat] coords.
 */
export function accumulateDistances(coords: [number, number][]): number[] {
  const cum: number[] = [0];
  for (let i = 1; i < coords.length; i++) {
    cum.push(cum[i - 1] + haversineMeters(coords[i - 1], coords[i]));
  }
  return cum;
}

// ── Segment splitting ─────────────────────────────────────────────────────────

/**
 * Find the index of the geometry vertex closest (Euclidean in degrees) to `target`.
 * Search is restricted to `[startIdx, endIdx)` to keep splits monotonic.
 */
function closestVertexIndex(
  geom: [number, number][],
  target: [number, number],
  startIdx: number,
  endIdx: number,
): number {
  let bestIdx = startIdx;
  let bestDist = Infinity;
  for (let i = startIdx; i < endIdx; i++) {
    const dx = geom[i][0] - target[0];
    const dy = geom[i][1] - target[1];
    const d = dx * dx + dy * dy;
    if (d < bestDist) { bestDist = d; bestIdx = i; }
  }
  return bestIdx;
}

/**
 * Split a flat OSRM geometry into per-leg RouteSegments.
 * Falls back to straight-line 2-point segments when geometry is null
 * or when a split is degenerate.
 */
export function buildSegments(
  waypoints: Array<{ lat: number; lng: number }>,
  routeGeometry: [number, number][] | null,
): RouteSegment[] {
  if (waypoints.length < 2) return [];

  const segments: RouteSegment[] = [];
  let cumulativeStart = 0;

  if (!routeGeometry || routeGeometry.length < 2) {
    // Straight-line fallback
    for (let i = 0; i < waypoints.length - 1; i++) {
      const from: [number, number] = [waypoints[i].lng,     waypoints[i].lat];
      const to:   [number, number] = [waypoints[i + 1].lng, waypoints[i + 1].lat];
      const cum = accumulateDistances([from, to]);
      const len = cum[cum.length - 1];
      segments.push({
        fromWaypointIndex: i,
        toWaypointIndex:   i + 1,
        coords:            [from, to],
        lengthMeters:      len,
        startMeters:       cumulativeStart,
        endMeters:         cumulativeStart + len,
        cumulativeMeters:  cum,
      });
      cumulativeStart += len;
    }
    return segments;
  }

  // Split the OSRM geometry at closest vertices to each intermediate waypoint
  const splitIndices: number[] = [0];
  for (let i = 1; i < waypoints.length - 1; i++) {
    const target: [number, number] = [waypoints[i].lng, waypoints[i].lat];
    // Search only the portion after the previous split to keep splits monotonic
    const from = splitIndices[splitIndices.length - 1] + 1;
    const to   = routeGeometry.length - 1;
    const idx  = closestVertexIndex(routeGeometry, target, from, to);
    splitIndices.push(idx);
  }
  splitIndices.push(routeGeometry.length - 1);

  for (let i = 0; i < splitIndices.length - 1; i++) {
    let start = splitIndices[i];
    let end   = splitIndices[i + 1];

    // Degenerate split: fall back to straight line for this leg
    if (end <= start) {
      const from: [number, number] = [waypoints[i].lng,     waypoints[i].lat];
      const to:   [number, number] = [waypoints[i + 1].lng, waypoints[i + 1].lat];
      const cum = accumulateDistances([from, to]);
      const len = cum[cum.length - 1];
      segments.push({
        fromWaypointIndex: i,
        toWaypointIndex:   i + 1,
        coords:            [from, to],
        lengthMeters:      len,
        startMeters:       cumulativeStart,
        endMeters:         cumulativeStart + len,
        cumulativeMeters:  cum,
      });
      cumulativeStart += len;
      continue;
    }

    const coords = routeGeometry.slice(start, end + 1) as [number, number][];
    const cum    = accumulateDistances(coords);
    const len    = cum[cum.length - 1];

    segments.push({
      fromWaypointIndex: i,
      toWaypointIndex:   i + 1,
      coords,
      lengthMeters:      len,
      startMeters:       cumulativeStart,
      endMeters:         cumulativeStart + len,
      cumulativeMeters:  cum,
    });
    cumulativeStart += len;
  }

  return segments;
}

// ── Interpolation ─────────────────────────────────────────────────────────────

/** Binary search: largest index i where cum[i] <= target. */
function binarySearch(cum: number[], target: number): number {
  let lo = 0, hi = cum.length - 2;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (cum[mid] <= target) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/**
 * Interpolate vehicle position along a segment given meters traveled into it.
 * Uses binary search + lerp for O(log n) performance.
 */
export function interpolateAlongSegment(
  segment: RouteSegment,
  metersIntoSegment: number,
  prevBearing = 0,
): VehiclePosition {
  const m = Math.max(0, Math.min(metersIntoSegment, segment.lengthMeters));
  const cum = segment.cumulativeMeters;
  const coords = segment.coords;

  const i = binarySearch(cum, m);
  const span = cum[i + 1] - cum[i];
  const t    = span > 0 ? (m - cum[i]) / span : 0;

  const lng = coords[i][0] + (coords[i + 1][0] - coords[i][0]) * t;
  const lat = coords[i][1] + (coords[i + 1][1] - coords[i][1]) * t;

  const bearing = (span > 1e-6)
    ? computeBearing(coords[i], coords[i + 1], prevBearing)
    : prevBearing;

  return { lng, lat, bearing };
}

// ── Curvature analysis ────────────────────────────────────────────────────────

/**
 * Estimate local curvature 0 (straight) → 1 (sharp bend).
 * Samples ±lookAheadMeters from current position and measures bearing change.
 */
export function estimateCurvature(
  segment: RouteSegment,
  metersIntoSegment: number,
  lookAheadMeters = 150,
): number {
  const behind = interpolateAlongSegment(segment, metersIntoSegment - lookAheadMeters);
  const current = interpolateAlongSegment(segment, metersIntoSegment);
  const ahead  = interpolateAlongSegment(segment, metersIntoSegment + lookAheadMeters);

  const bearingIn  = computeBearing([behind.lng, behind.lat],  [current.lng, current.lat]);
  const bearingOut = computeBearing([current.lng, current.lat], [ahead.lng,  ahead.lat]);

  const delta = Math.abs(normaliseDelta(bearingOut - bearingIn));
  return Math.min(delta / 60, 1); // 60° = fully curved
}

// ── Camera parameter resolver ─────────────────────────────────────────────────

const BASE_ZOOM: Record<TravelMode, number> = {
  driving:   16.5,
  walking:   17.0,
  bicycling: 16.8,
  transit:   16.5,
};

/**
 * Zoom delta (added to the base driving/cinematic zoom) based on how long this
 * leg is: ~3km keeps the normal zoom, a 100km leg pulls back hard (so the
 * movement across it is visible), a <1km leg zooms in for detail.
 * Clamped so legs of any length still produce a sane on-screen camera.
 */
function distanceZoomDelta(lengthMeters: number): number {
  const km = Math.max(lengthMeters / 1000, 0.05);
  return Math.max(-5, Math.min(1.5, -Math.log2(km / 3)));
}

/**
 * Given the current animation state, return camera parameters for this frame.
 * All durations are 0 — smoothness comes entirely from the IIR filters in useTourRoute,
 * not from MapLibre's own animation system (which creates abort-restart jitter at 60fps).
 *
 * @param vehiclePos  Smoothed vehicle position (bearing already IIR-filtered).
 * @param cameraCenter  Smoothed camera look-at point (may differ from vehiclePos in cinematic mode).
 */
export function getCameraParams(
  vehiclePos: VehiclePosition,
  cameraCenter: { lng: number; lat: number },
  segment: RouteSegment,
  metersIntoSegment: number,
  cameraMode: CameraMode,
  travelMode: TravelMode,
  curvature: number,
  elapsedSeconds: number,
): CameraParams {
  const baseZoom = BASE_ZOOM[travelMode] - 1.5 + distanceZoomDelta(segment.lengthMeters);

  switch (cameraMode) {
    case "follow":
      return {
        center:   [cameraCenter.lng, cameraCenter.lat],
        zoom:     baseZoom,
        pitch:    50 + curvature * 8,
        bearing:  vehiclePos.bearing,
        duration: 0,
      };

    case "cinematic": {
      const sway = Math.sin(elapsedSeconds / 5) * 6 * curvature;
      return {
        center:   [cameraCenter.lng, cameraCenter.lat],
        zoom:     baseZoom - 0.5 - curvature * 0.6,
        pitch:    58 + curvature * 12,
        bearing:  vehiclePos.bearing + sway,
        duration: 0,
      };
    }

    case "overview": {
      const mid = interpolateAlongSegment(segment, segment.lengthMeters / 2);
      // Dynamic zoom so the segment fills ~55 % of a typical 1200 px viewport.
      // At zoom 13 that viewport shows ≈ 23 km; scaling from there:
      //   zoom = 13 + log2(TARGET_KM / segmentKm), TARGET_KM ≈ 12.7 km (55 % of 23)
      // Clamped to [10, 16] to stay readable.
      const lengthKm = segment.lengthMeters / 1000;
      const zoom = Math.max(10, Math.min(16, 13 + Math.log2(9.8 / Math.max(lengthKm, 0.1))));
      return {
        center:   [mid.lng, mid.lat],
        zoom,
        pitch:    20,
        bearing:  0,
        duration: 0,
      };
    }
  }
}

// ── Route bounding box ────────────────────────────────────────────────────────

/**
 * Compute [west, south, east, north] bounding box over all segment coordinates.
 */
export function computeRouteBounds(segments: RouteSegment[]): [number, number, number, number] {
  let west =  Infinity, south =  Infinity;
  let east = -Infinity, north = -Infinity;
  for (const seg of segments) {
    for (const [lng, lat] of seg.coords) {
      if (lng < west)  west  = lng;
      if (lng > east)  east  = lng;
      if (lat < south) south = lat;
      if (lat > north) north = lat;
    }
  }
  return [west, south, east, north];
}
